import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatDate } from '@/lib/utils'
import { Calendar, MapPin, Users, Edit3, Trash2, Plus, CheckCircle2, Link2 } from 'lucide-react'
import { toast } from 'sonner'
import MeetingModal from '@/components/MeetingModal'
import { STATUS_META, isUrl, setMeetingStatus, deleteMeeting, type MeetingRow, type MeetingEntityType } from '@/lib/meetings'

/**
 * MeetingsThread — scheduled meetings on a deal / contact / company, over the
 * `sales_meetings` table (Sales Calendar CP1). Query by entity_type+entity_id,
 * a list with status + complete/edit/delete, create/edit via the shared
 * MeetingModal. Completion of a deal-linked meeting logs a deal_activities
 * meeting_logged row (see setMeetingStatus). RLS enforces tenant isolation.
 */

interface Props {
  entityType: MeetingEntityType
  entityId: string
}

const queryKey = (type: string, id: string) => ['sales-meetings', type, id] as const

export default function MeetingsThread({ entityType, entityId }: Props) {
  const queryClient = useQueryClient()
  const { profile, workspace } = useAuth()
  const meId = profile?.id ?? null
  const workspaceId = workspace?.id ?? null

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<MeetingRow | null>(null)

  const { data: meetings = [], isLoading } = useQuery<MeetingRow[]>({
    queryKey: queryKey(entityType, entityId),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('sales_meetings')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('start_time', { ascending: true })
      if (error) throw error
      return (data ?? []) as MeetingRow[]
    },
    enabled: !!entityId,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKey(entityType, entityId) })
    if (entityType === 'deal') queryClient.invalidateQueries({ queryKey: ['deal-activities', entityId] })
  }

  const statusMut = useMutation({
    mutationFn: (vars: { m: MeetingRow; status: 'completed' }) => setMeetingStatus(vars.m, vars.status, { workspaceId: workspaceId! }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || 'Failed to update meeting'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteMeeting(id),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || 'Failed to delete meeting'),
  })

  const openCreate = () => { setEditing(null); setModalOpen(true) }
  const openEdit = (m: MeetingRow) => { setEditing(m); setModalOpen(true) }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button type="button" onClick={openCreate}
        style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 7, color: 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        <Plus style={{ width: 12, height: 12 }} /> Schedule meeting
      </button>

      {isLoading ? (
        <div style={{ padding: 4, color: 'var(--ink-tertiary)', fontSize: 12 }}>Loading meetings…</div>
      ) : meetings.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No meetings</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <AnimatePresence initial={false}>
            {meetings.map(m => {
              const meta = STATUS_META[m.status] || STATUS_META.scheduled
              const done = m.status === 'completed'
              return (
                <motion.div key={m.id}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.14 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Calendar style={{ width: 14, height: 14, color: meta.color, flexShrink: 0, marginTop: 2 }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: done ? 'var(--ink-tertiary)' : 'var(--ink-primary)' }}>{m.title}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.04em', color: meta.color, background: `color-mix(in srgb, ${meta.color} var(--status-tint-dim), transparent)` }}>{meta.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 11, color: 'var(--ink-tertiary)', paddingLeft: 22 }}>
                    <span>{formatDate(m.start_time, 'MMM d, h:mm a')} – {formatDate(m.end_time, 'h:mm a')}</span>
                    {m.location && (
                      isUrl(m.location)
                        ? <a href={m.location} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--accent-text)', textDecoration: 'none' }}><Link2 style={{ width: 10, height: 10 }} /> Join</a>
                        : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin style={{ width: 10, height: 10 }} /> {m.location}</span>
                    )}
                    {m.attendee_emails?.length > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Users style={{ width: 10, height: 10 }} /> {m.attendee_emails.length}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 22 }}>
                    {m.status === 'scheduled' && (
                      <button type="button" onClick={() => statusMut.mutate({ m, status: 'completed' })}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--positive)', fontSize: 11, fontWeight: 600, padding: 0 }}>
                        <CheckCircle2 style={{ width: 12, height: 12 }} /> Complete
                      </button>
                    )}
                    <button type="button" onClick={() => openEdit(m)} aria-label="Edit meeting"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, padding: 0 }}>
                      <Edit3 style={{ width: 12, height: 12 }} /> Edit
                    </button>
                    <button type="button" onClick={() => { if (window.confirm('Delete this meeting?')) deleteMut.mutate(m.id) }} aria-label="Delete meeting"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, padding: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                      <Trash2 style={{ width: 12, height: 12 }} /> Delete
                    </button>
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}

      <MeetingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={invalidate}
        workspaceId={workspaceId!}
        ownerId={meId}
        entityType={entityType}
        entityId={entityId}
        editing={editing}
      />
    </div>
  )
}
